if (!customElements.get('xclsior-product-information')) {
  customElements.define(
    'xclsior-product-information',
    class XclsiorProductInformation extends HTMLElement {
      constructor() {
        super();

        this.currentMediaIndex = 0;
        this.currentOfferIndex = 0;
        this.variantChangeUnsubscriber = null;

        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.mainImage = this.querySelector('[data-xpi-main-image]');
        this.thumbnails = [...this.querySelectorAll('[data-xpi-thumb]')];
        this.offerSlides = [...this.querySelectorAll('[data-xpi-offer-slide]')];
        this.offerTrack = this.querySelector('[data-xpi-offer-track]');

        const activeThumbnailIndex = this.thumbnails.findIndex((thumbnail) =>
          thumbnail.classList.contains('is-active')
        );

        this.currentMediaIndex = activeThumbnailIndex >= 0 ? activeThumbnailIndex : 0;

        this.bindGalleryControls();
        this.bindOfferControls();
        this.bindCouponControls();

        this.addEventListener('keydown', this.handleKeydown);

        /*
         * Dawn publishes every successful variant selection through PubSub.
         * product-info.js continues to update price, URL, availability and
         * the hidden .product-variant-id input. This component only syncs
         * the custom gallery with the selected variant media.
         */
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange
          );
        } else {
          console.warn(
            'Xclsior product information: Dawn PubSub was not available.'
          );
        }
      }

      disconnectedCallback() {
        this.removeEventListener('keydown', this.handleKeydown);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
      }

      bindGalleryControls() {
        this.querySelector('[data-xpi-prev]')?.addEventListener('click', () => {
          this.changeMedia(-1);
        });

        this.querySelector('[data-xpi-next]')?.addEventListener('click', () => {
          this.changeMedia(1);
        });

        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => {
            this.showMedia(index);
          });
        });
      }

      bindOfferControls() {
        this.querySelector('[data-xpi-offer-prev]')?.addEventListener('click', () => {
          this.changeOffer(-1);
        });

        this.querySelector('[data-xpi-offer-next]')?.addEventListener('click', () => {
          this.changeOffer(1);
        });
      }

      bindCouponControls() {
        this.querySelectorAll('[data-xpi-copy]').forEach((button) => {
          button.addEventListener('click', () => {
            this.copyCoupon(button);
          });
        });
      }

      handleVariantChange(event) {
        const eventData = event?.data || {};
        const eventSectionId = String(eventData.sectionId || '');
        const componentSectionId = String(this.dataset.sectionId || '');

        if (eventSectionId && eventSectionId !== componentSectionId) {
          return;
        }

        const variant = eventData.variant;
        const mediaId = variant?.featured_media?.id;

        if (!mediaId) {
          return;
        }

        const thumbnailIndex = this.thumbnails.findIndex(
          (thumbnail) => Number(thumbnail.dataset.mediaId) === Number(mediaId)
        );

        if (thumbnailIndex >= 0) {
          this.showMedia(thumbnailIndex);
        }
      }

      handleKeydown(event) {
        const galleryStage = this.querySelector('[data-xpi-stage]');

        if (!galleryStage?.contains(document.activeElement)) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeMedia(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeMedia(1);
        }
      }

      changeMedia(direction) {
        if (!this.thumbnails.length) {
          return;
        }

        const newIndex =
          (this.currentMediaIndex + direction + this.thumbnails.length) %
          this.thumbnails.length;

        this.showMedia(newIndex);
      }

      showMedia(index) {
        const thumbnail = this.thumbnails[index];

        if (!thumbnail || !this.mainImage) {
          return;
        }

        const imageSource = thumbnail.dataset.imageSrc;

        if (!imageSource) {
          return;
        }

        this.currentMediaIndex = index;
        this.mainImage.classList.add('is-changing');

        const preloadedImage = new Image();

        preloadedImage.onload = () => {
          this.mainImage.src = imageSource;
          this.mainImage.srcset = '';
          this.mainImage.alt = thumbnail.dataset.imageAlt || '';
          this.mainImage.classList.remove('is-changing');
        };

        preloadedImage.onerror = () => {
          this.mainImage.classList.remove('is-changing');
        };

        preloadedImage.src = imageSource;

        this.thumbnails.forEach((thumbnailItem, thumbnailIndex) => {
          const isActive = thumbnailIndex === index;

          thumbnailItem.classList.toggle('is-active', isActive);
          thumbnailItem.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        thumbnail.scrollIntoView({
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }

      changeOffer(direction) {
        if (this.offerSlides.length < 2 || !this.offerTrack) {
          return;
        }

        this.currentOfferIndex =
          (this.currentOfferIndex + direction + this.offerSlides.length) %
          this.offerSlides.length;

        this.offerTrack.style.transform = `translateX(-${this.currentOfferIndex * 100}%)`;
      }

      async copyCoupon(button) {
        const couponCode = button.dataset.code?.trim() || '';

        if (!couponCode) {
          return;
        }

        const status = this.querySelector('[data-xpi-copy-status]');
        const copyText = button.querySelector('.xpi__copy-text');

        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(couponCode);
          } else {
            this.copyUsingFallback(couponCode);
          }

          this.showCopiedStatus(copyText, status, couponCode);
        } catch (error) {
          this.copyUsingFallback(couponCode);
          this.showCopiedStatus(copyText, status, couponCode);
        }
      }

      copyUsingFallback(value) {
        const temporaryInput = document.createElement('textarea');

        temporaryInput.value = value;
        temporaryInput.setAttribute('readonly', '');
        temporaryInput.style.position = 'fixed';
        temporaryInput.style.top = '-9999px';
        temporaryInput.style.left = '-9999px';
        temporaryInput.style.opacity = '0';

        document.body.appendChild(temporaryInput);
        temporaryInput.focus();
        temporaryInput.select();
        document.execCommand('copy');
        temporaryInput.remove();
      }

      showCopiedStatus(copyText, status, couponCode) {
        if (copyText) {
          copyText.textContent = 'Copied';
        }

        if (status) {
          status.textContent = `Coupon code ${couponCode} copied.`;
        }

        window.setTimeout(() => {
          if (copyText) {
            copyText.textContent = 'Copy';
          }

          if (status) {
            status.textContent = '';
          }
        }, 1800);
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
